import Cookies from 'js-cookie';
import { History } from 'history';

import api, { configuracaoPadrao } from './api';
import { obterIdSessaoDaUnidade } from './acessoPainel';
import { corpoDaApi } from './erroApi';
import { metricsRegister } from './metrics';
import { TokenProps } from '../infra/interfaces';
import { default as packageData } from '../../package.json';

const LOCAL_STORAGE_KEYS = ['context-on-premises', 'onPremises:health', 'url', 'id-sessao'];
const COOKIES_DA_SESSAO = ['token', 'refreshToken', 'tokenExpiration'];

/** js-cookie 2 conta a validade em dias; acompanha o RefreshTokenValidFor do OnPremises. */
const DIAS_DO_REFRESH_TOKEN = 30;
const OITO_HORAS_EM_SEGUNDOS = 28800;
const MS_POR_SEGUNDO = 1000;
const TRAVA_DA_RENOVACAO = 'painel:renovar-sessao';

/** O servidor recusou o refresh token: a sessão acabou e só um novo login resolve. */
export class SessaoRecusadaError extends Error {}

/** Rede fora ou servidor com erro: a sessão pode continuar valendo, vale tentar de novo. */
export class FalhaTemporariaError extends Error {}

type TravasDoNavegador = {
  request: <T>(nome: string, tarefa: () => Promise<T>) => Promise<T>;
};

/** O usuário que o login grava em `context-on-premises`. */
export type UsuarioDaSessao = {
  nome?: string;
  usuario?: string;
  email?: string;
  tipoUsuario?: number;
  unidade?: string;
  /**
   * Gravado só quando o JWT traz a claim funcionarioInside: o OnPremises da
   * unidade já abre o Designer para o administrador do painel.
   */
  designerCliente?: boolean;
};

/** Sem sessão ou com o JSON corrompido, devolve null em vez de derrubar a tela. */
export function usuarioDaSessao(): UsuarioDaSessao | null {
  try {
    const contexto = JSON.parse(localStorage.getItem('context-on-premises') ?? 'null');

    return contexto && typeof contexto === 'object' ? contexto : null;
  } catch (erro) {
    return null;
  }
}

export function gravarSessao({ accessToken, expires, refreshToken }: TokenProps): void {
  const vencimento = Date.now() + (expires || OITO_HORAS_EM_SEGUNDOS) * MS_POR_SEGUNDO;

  Cookies.set('token', accessToken, { expires: new Date(vencimento) });
  Cookies.set('tokenExpiration', String(Math.round(vencimento / MS_POR_SEGUNDO)), {
    expires: DIAS_DO_REFRESH_TOKEN,
  });

  if (refreshToken) {
    Cookies.set('refreshToken', refreshToken, { expires: DIAS_DO_REFRESH_TOKEN });
  } else {
    Cookies.remove('refreshToken');
  }
}

/**
 * Cada refresh token só vale uma troca. Duas abas trocando o mesmo token ao mesmo
 * tempo recebem dois tokens novos e o servidor revoga um deles; se a aba que grava
 * o cookie por último for a do revogado, a renovação seguinte desloga. A trava do
 * navegador põe as abas em fila; sem ela (navegador antigo), resta a checagem do
 * cookie depois da recusa.
 */
function naFilaEntreAbas<T>(tarefa: () => Promise<T>): Promise<T> {
  const travas = (navigator as unknown as { locks?: TravasDoNavegador }).locks;

  return travas ? travas.request(TRAVA_DA_RENOVACAO, tarefa) : tarefa();
}

/**
 * O id-sessao do gateway é pego no login e vence em 24 h; passado isso, toda
 * chamada (o refresh também) volta 401 do gateway. Renovar junto de cada
 * refresh mantém a sessão viva pelos 30 dias do refresh token.
 */
async function renovarIdSessao(): Promise<void> {
  const contexto = JSON.parse(localStorage.getItem('context-on-premises') ?? 'null');

  if (!contexto?.unidade) return;

  try {
    localStorage.setItem('id-sessao', await obterIdSessaoDaUnidade(contexto.unidade));
  } catch (erro: any) {
    throw new FalhaTemporariaError(erro?.message ?? 'Falha ao renovar o id-sessao.');
  }
}

async function trocarRefreshToken(refreshTokenAntes: string | undefined): Promise<void> {
  const refreshToken = Cookies.get('refreshToken');

  if (!refreshToken) throw new SessaoRecusadaError('Sessão sem refresh token.');

  // Outra aba renovou enquanto esta esperava a vez: os cookies já estão novos.
  if (refreshToken !== refreshTokenAntes) return;

  await renovarIdSessao();

  let resposta: unknown;

  try {
    resposta = await api(configuracaoPadrao()).post(
      '/api/autenticacao/refresh',
      { refreshToken },
      { silenciarAvisos: true }
    );
  } catch (erro: any) {
    const status = erro?.response?.status;

    if (status && status < 500) throw new SessaoRecusadaError(`Refresh recusado (${status}).`);

    throw new FalhaTemporariaError(erro?.message ?? 'Falha ao renovar a sessão.');
  }

  const envelope = corpoDaApi<TokenProps>(resposta);

  if (envelope?.data?.accessToken) {
    gravarSessao(envelope.data);
    return;
  }

  if (Cookies.get('refreshToken') !== refreshToken) return;

  throw new SessaoRecusadaError(envelope?.notifications?.[0] ?? 'Refresh token inválido.');
}

let renovacaoEmAndamento: Promise<void> | undefined;

/**
 * Troca o refresh token do cookie por uma sessão nova. Resolve quando a sessão
 * está renovada (por esta aba ou por outra); rejeita com SessaoRecusadaError ou
 * FalhaTemporariaError. Chamadas simultâneas compartilham a mesma requisição.
 */
export function renovarSessao(): Promise<void> {
  if (!renovacaoEmAndamento) {
    const refreshTokenAntes = Cookies.get('refreshToken');

    renovacaoEmAndamento = naFilaEntreAbas(() => trocarRefreshToken(refreshTokenAntes)).finally(
      () => {
        renovacaoEmAndamento = undefined;
      }
    );
  }

  return renovacaoEmAndamento;
}

let encerramento: Promise<void> | undefined;

/**
 * Fim da sessão que o usuário não pediu (refresh recusado). Sai da tela antes de
 * apagar a sessão: com ela montada, as chamadas em andamento ficavam sem cookie e
 * sem url e o erro aparecia por cima do login. O reload descarta o que ficou
 * pendente e os caches do usuário; o `from` sobrevive porque o BrowserRouter
 * guarda o state no history do navegador.
 */
export function encerrarSessao(history: History): Promise<void> {
  if (!encerramento) {
    const { pathname, search, hash } = history.location;

    history.replace({ pathname: '/login', state: { from: { pathname, search, hash } } });
    encerramento = performLogout().then(() => history.go(0));
  }

  return encerramento;
}

export async function performLogout(): Promise<void> {
  const url = localStorage.getItem('url') as string;
  const token = Cookies.get('token') as string;

  try {
    await api({ url, token }).post('/api/autenticacao/logout', {
      versao: packageData.version,
      plataforma: 'painel-indicadores',
      refreshToken: Cookies.get('refreshToken'),
    });

    const user = localStorage.getItem('context-on-premises');
    const userData = user ? JSON.parse(user) : null;

    metricsRegister({
      acao: 'LOGOFF',
      usuario: userData?.usuario,
      email: userData?.email,
      unidade: 0,
    });
  } catch (error) {
    console.error('Não foi possível registrar o log de logout');
  } finally {
    COOKIES_DA_SESSAO.forEach((cookie) => Cookies.remove(cookie));
    LOCAL_STORAGE_KEYS.forEach((localStorageItem) => {
      localStorage.removeItem(localStorageItem);
    });
  }
}
